import { AdminPage } from "@/components/AdminPage";
import { AdminTable, type Column } from "@/components/AdminTable";
import { OverviewMetricChart } from "@/components/OverviewMetricChart";
import { AlertCircle, Check, CloudOff } from "lucide-react";

type Row = {
  id: string;
  email: string;
  name: string;
  cpf: string | null;
  phone: string | null;
  type: string;
  kyc_level: number;
  referral_code: string | null;
  stripe_customer_id: string | null;
  last_stripe_sync_at: string | null;
  last_stripe_sync_error: string | null;
  deleted_at: string | null;
  verifications_count: number;
  entities_count: number;
  pj_entities_count: number;
  referrals_total: number;
  referrals_paid: number;
  total_spent_cents: number;
  created_at: string;
  updated_at: string | null;
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T") + "Z").toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatCPF(d: string | null): string {
  if (!d || d.length !== 11) return d ?? "—";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

const COLUMNS: Column<Row>[] = [
  {
    header: "Usuário",
    width: "22%",
    cell: (r) => (
      <div>
        <p className="truncate font-medium text-text">
          {r.name}
          {r.deleted_at && (
            <span className="ml-2 inline-flex items-center rounded-full bg-surface-alt px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-text-tertiary">
              excluído
            </span>
          )}
        </p>
        <p className="truncate text-[11px] text-text-tertiary">{r.email}</p>
      </div>
    ),
  },
  {
    header: "CPF / Telefone",
    width: "14%",
    hideBelow: "sm",
    cell: (r) => (
      <div>
        <p className="truncate text-xs text-text-secondary">{formatCPF(r.cpf)}</p>
        <p className="truncate text-[11px] text-text-tertiary">{r.phone ?? "—"}</p>
      </div>
    ),
  },
  {
    header: "Tipo",
    width: "8%",
    hideBelow: "lg",
    cell: (r) => <span className="text-xs text-text-secondary">{r.type}</span>,
  },
  {
    header: "KYC",
    width: "7%",
    hideBelow: "lg",
    cell: (r) => (
      <span
        title={`Nível de KYC: ${r.kyc_level}`}
        className="text-xs text-text"
      >
        {r.kyc_level}
      </span>
    ),
  },
  {
    header: "PJs",
    width: "7%",
    hideBelow: "md",
    cell: (r) => (
      <span
        title={`${r.pj_entities_count} empresa(s) cadastradas. PF padrão não contada.`}
        className="text-xs text-text"
      >
        {r.pj_entities_count}
      </span>
    ),
  },
  {
    header: "Indicações",
    width: "10%",
    hideBelow: "xl",
    cell: (r) => (
      <div>
        <p className="text-xs text-text">
          {r.referrals_paid}
          <span className="text-text-tertiary"> / {r.referrals_total}</span>
        </p>
        <p className="truncate font-mono text-[10px] text-text-tertiary">
          {r.referral_code ?? "—"}
        </p>
      </div>
    ),
  },
  {
    header: "Total gasto",
    width: "9%",
    hideBelow: "md",
    cell: (r) => (
      <span className="text-xs font-medium text-text">
        {formatBRL(r.total_spent_cents ?? 0)}
      </span>
    ),
  },
  {
    header: "Stripe",
    width: "12%",
    hideBelow: "lg",
    cell: (r) => <StripeSyncBadge row={r} />,
  },
  {
    header: "Criado",
    width: "11%",
    hideBelow: "sm",
    cell: (r) => (
      <span className="text-[11px] text-text-secondary">{formatDate(r.created_at)}</span>
    ),
  },
];

/**
 * Three-state Stripe sync indicator.
 *   - synced  → green check, tooltip shows last_stripe_sync_at + customer id
 *   - error   → red alert, tooltip carries the persisted error string
 *   - pending → muted CloudOff, never synced
 */
function StripeSyncBadge({ row }: { row: Row }) {
  if (row.last_stripe_sync_error) {
    return (
      <span
        title={`Última falha: ${row.last_stripe_sync_error}`}
        className="inline-flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-danger"
      >
        <AlertCircle size={11} strokeWidth={2} aria-hidden />
        Erro
      </span>
    );
  }
  if (row.stripe_customer_id && row.last_stripe_sync_at) {
    return (
      <span
        title={`Sincronizado em ${formatDate(row.last_stripe_sync_at)} · ${row.stripe_customer_id}`}
        className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent"
      >
        <Check size={11} strokeWidth={2} aria-hidden />
        Sincronizado
      </span>
    );
  }
  if (row.stripe_customer_id) {
    return (
      <span
        title={`Customer ${row.stripe_customer_id} criado mas sem timestamp de sync. Próxima execução do cron preenche.`}
        className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-warning"
      >
        <CloudOff size={11} strokeWidth={2} aria-hidden />
        Pendente
      </span>
    );
  }
  return (
    <span
      title="Sem Customer no Stripe. Será criado no próximo login ou via cron diário."
      className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-tertiary"
    >
      <CloudOff size={11} strokeWidth={2} aria-hidden />
      Não criado
    </span>
  );
}

export default function AdminUsuariosPage() {
  return (
    <AdminPage
      eyebrow="Usuários"
      title="Cadastros"
      description="Todos os usuários do PodeContratar. Use a busca pra filtrar por nome, e-mail ou CPF."
    >
      <div className="mb-6">
        <OverviewMetricChart
          metric="users"
          title="Cadastros nos últimos 30 dias"
        />
      </div>
      <AdminTable<Row>
        endpoint="/api/admin/users"
        itemsKey="users"
        rowKey={(r) => r.id}
        columns={COLUMNS}
        searchPlaceholder="Nome, e-mail ou CPF"
        emptyTitle="Nenhum usuário cadastrado"
      />
    </AdminPage>
  );
}
